import { MemberUtils } from "@shared/member.utils";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { BackButton } from "../../components/common/BackButton";
import { Button } from "../../components/common/Button";
import { PageTitle } from "../../components/common/PageData";
import { useCourseStore } from "../../stores/course.store";
import { useSeasonStore } from "../../stores/season.store";
import { useUserStore } from "../../stores/user.store";
import { getMemberIcon, getMemberTypeLabel } from "../../styles/member";
import { CoursePaymentLayout, CoursePendingIntro } from "./CoursePendingRecap";
import {
  ActionButton,
  ButtonContainer,
  MemberCard,
  MemberContent,
  MemberInfo,
  MemberName,
  MemberTypeLabel,
  MembershipInfo,
  MembershipLabel,
  MembershipStatus,
  PriceLabel,
} from "./components/MemberCard";
import { CoursePaymentNotice } from "./CoursePaymentNotice";
import { useCourseSubscriptionContext } from "./useCourseSubscriptionContext";

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;


const ClosedMessage = styled.div`
  background-color: #fff3cd;
  border-radius: 8px;
  border: 1px solid #ffc107;
  text-align: center;
  font-size: 16px;
  color: #856404;
  padding: 16px;
  margin: 20px 0;
`;


const TotalSection = styled.div`
  background-color: #e6f3ff;
  padding: 16px;
  margin: 20px 0;
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  font-size: 18px;
  border-radius: 8px;
`;

export const Courses = () => {
  const navigate = useNavigate();
  const { profile, loadProfile } = useUserStore();
  const { currentSeason } = useSeasonStore();
  const { currentCourse, getCurrentCourse, isLoading } = useCourseStore();
  const { tempSubscriptions } = useCourseSubscriptionContext();

  useEffect(() => {
    if (currentSeason) {
      getCurrentCourse();
      loadProfile();
    }
  }, [currentSeason, getCurrentCourse, loadProfile]);

  const unreservedSelections = useMemo(() => {
    if (!currentCourse || !profile) {
      return [];
    }
    return Array.from(tempSubscriptions.values()).filter((selection) => {
      const member = profile.members.find((item) => item.id === selection.subscriber_id);
      return selection.course_id === currentCourse.id && member &&
        !member.course_subscriptions.some((existing) => existing.course_id === currentCourse.id);
    });
  }, [tempSubscriptions, currentCourse, profile]);
  const indicativeTotal = unreservedSelections.length * (currentCourse?.price || 0);

  // Determine subscription status based on dates
  const getSubscriptionStatus = (): "before_opening" | "open" | "closed" => {
    if (!currentCourse) return "closed";
    const now = Date.now();
    const startDate = new Date(currentCourse.subscription_start_date).getTime();
    const endDate = new Date(currentCourse.subscription_end_date).getTime();

    if (now < startDate) return "before_opening";
    if (now > endDate) return "closed";
    return "open";
  };

  const subscriptionStatus = getSubscriptionStatus();
  const hasPending = !!currentCourse && !!profile?.members.some((member) =>
    member.course_subscriptions?.some((subscription) =>
      subscription.course_id === currentCourse.id && subscription.status === "pending"
    )
  );
  const hasUnsubscribedMembers = !!currentCourse && !!profile?.members.some((member) =>
    !member.course_subscriptions?.some((subscription) => subscription.course_id === currentCourse.id)
  );

  const showSubscription = (memberId: string) => {
    navigate(`/account/courses/${memberId}`);
  };

  const handleReviewReservation = () => {
    if (unreservedSelections.length === 0 || subscriptionStatus !== "open") return;
    navigate("/account/courses/checkout");
  };

  if (!profile) {
    return <div>Loading...</div>;
  }

  if (!currentSeason) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate("/account")} />
        <PageTitle>CORSI E ISCRIZIONE CORSI</PageTitle>
        <ClosedMessage>
          Le iscrizioni ai corsi sono temporaneamente chiuse.
        </ClosedMessage>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate("/account")} />
        <PageTitle>CORSI E ISCRIZIONE CORSI</PageTitle>
        <p>Caricamento...</p>
      </PageContainer>
    );
  }

  if (!currentCourse) {
    return (
      <PageContainer>
        <BackButton />
        <PageTitle>CORSI E ISCRIZIONE CORSI</PageTitle>
        <ClosedMessage>
          Non ci sono corsi disponibili per la stagione corrente.
        </ClosedMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate("/account")} />
      <PageTitle>{currentCourse.title.toUpperCase()}</PageTitle>
      {!hasPending && subscriptionStatus === "open" && hasUnsubscribedMembers && <CoursePaymentNotice />}

      {/* After Closing: Show closed message and poster */}
      {subscriptionStatus === "closed" && (
        <>
          <ClosedMessage>
            Le iscrizioni ai corsi sono attualmente chiuse.
          </ClosedMessage>
        </>
      )}

      {hasPending && <CoursePendingIntro />}

      {/* Family Members List - Only show when open or closed (not before opening) */}
      {subscriptionStatus !== "before_opening" &&
        profile.members
          .sort(
            (a, b) =>
              new Date(a.created_at ?? 0).getTime() -
              new Date(b.created_at ?? 0).getTime()
          )
          .filter((member) => {
            // When closed, only show members with subscriptions or companions
            if (subscriptionStatus === "closed") {
              const courseSubscription = member.course_subscriptions?.find(
                (sub) => sub.course_id === currentCourse.id
              );

              // Check for temporary subscription
              const tempSubscription = tempSubscriptions.get(member.id);

              // Check if member is a companion in DB subscription
              const isCompanionDb = profile.members.some((m) =>
                m.course_subscriptions?.some(
                  (sub) =>
                    sub.course_id === currentCourse.id &&
                    sub.companion_id === member.id
                )
              );

              // Check if member is a companion in temp subscription
              const isCompanionTemp = Array.from(
                tempSubscriptions.entries()
              ).some(([_, sub]) => sub.companion_id === member.id);

              return (
                courseSubscription ||
                tempSubscription ||
                isCompanionDb ||
                isCompanionTemp
              );
            }
            return true; // Show all members when open
          })
          .map((member) => {
            const memberType = MemberUtils.getMemberType(
              member.date_of_birth,
              currentSeason
            );

            // Check if member has a valid season subscription
            const hasSeasonSubscription = member.subscriptions?.some(
              (sub) => sub.season_name === currentSeason.name
            );

            // Find DB course subscription for this member
            const courseSubscription = member.course_subscriptions?.find(
              (sub) => sub.course_id === currentCourse.id
            );

            // Check for temporary subscription
            const tempSubscription = tempSubscriptions.get(member.id);

            // Check if member is a companion in DB subscription and find who they're accompanying
            const companionForDb = profile.members.find((m) =>
              m.course_subscriptions?.some(
                (sub) =>
                  sub.course_id === currentCourse.id &&
                  sub.companion_id === member.id
              )
            );

            // Check if member is a companion in temp subscription and find who they're accompanying
            const companionForTempEntry = Array.from(
              tempSubscriptions.entries()
            ).find(([_, sub]) => sub.companion_id === member.id);
            const companionForTemp = companionForTempEntry
              ? profile.members.find((m) => m.id === companionForTempEntry[0])
              : undefined;

            const companionFor = companionForDb || companionForTemp;
            const isCompanion = !!companionFor;

            // Determine status
            let status: "active" | "companion" | "pending" = "companion";
            let statusText = "Non iscritto";
            let buttonText = subscriptionStatus === 'open' ? "ISCRIVITI AL CORSO" : " ";
            let buttonVariant: "black" | "orange" | "red" | "green" = "red";

            // Check if member needs season subscription first
            const needsSeasonSubscription =
              !hasSeasonSubscription &&
              !courseSubscription &&
              !tempSubscription &&
              !isCompanion;

            if (courseSubscription) {
              status = "active";
              statusText = courseSubscription.status === "pending" ? "In attesa di verifica" : "Confermato";
              status = courseSubscription.status === "pending" ? "pending" : "active";
              buttonText = "MOSTRA DETTAGLI";
              buttonVariant = "black";
            } else if (tempSubscription) {
              status = "pending";
              statusText = "Iscrizione non ancora inviata";
              buttonText = "MODIFICA ISCRIZIONE";
              buttonVariant = "orange";
            } else if (needsSeasonSubscription) {
              statusText =
                "Tessera stagionale richiesta per iscriversi al corso";
              buttonText = "FAI LA TESSERA STAGIONALE";
              buttonVariant = "black";
            }

            return (
              <MemberCard key={member.id}>
                <MemberContent>
                  <MemberInfo>
                    <MemberName>
                      {member.first_name} {member.last_name}
                    </MemberName>
                    <MemberTypeLabel>
                      {getMemberTypeLabel(memberType)}
                    </MemberTypeLabel>
                  </MemberInfo>
                  <img
                    src={getMemberIcon(memberType)}
                    alt={getMemberTypeLabel(memberType)}
                    width="40"
                    height="40"
                  />
                </MemberContent>

                <MembershipInfo>
                  <CoursePaymentLayout member={member} subscription={courseSubscription}>
                    {courseSubscription ? (
                      <>
                        <MembershipLabel>
                          {courseSubscription.type === "ski"
                            ? "SCI"
                            : "SNOWBOARD"}{" "}
                          • LIVELLO {courseSubscription.level}
                        </MembershipLabel>
                        <PriceLabel>{currentCourse.price.toFixed(2)} €</PriceLabel>
                        {courseSubscription.companion_id && (
                          <MembershipLabel>
                            Accomp:{" "}
                            {
                              profile.members.find(
                                (m) => m.id === courseSubscription.companion_id
                              )?.first_name
                            }
                          </MembershipLabel>
                        )}
                      </>
                    ) : tempSubscription ? (
                      <>
                        <MembershipLabel>
                          {tempSubscription.type === "ski" ? "SCI" : "SNOWBOARD"}{" "}
                          • LIVELLO {tempSubscription.level}
                        </MembershipLabel>
                        {tempSubscription.companion_id && (
                          <MembershipLabel>
                            Accomp:{" "}
                            {
                              profile.members.find(
                                (m) => m.id === tempSubscription.companion_id
                              )?.first_name
                            }
                          </MembershipLabel>
                        )}
                        <PriceLabel>
                          {currentCourse.price.toFixed(2)} €
                        </PriceLabel>
                      </>
                    ) : subscriptionStatus === 'closed' ? <></> : (
                      <MembershipLabel>NON ISCRITTO</MembershipLabel>
                    )}
                    <MembershipStatus $status={status}>
                      {statusText}
                    </MembershipStatus>
                  </CoursePaymentLayout>
                </MembershipInfo>

                <ButtonContainer>
                  {status === "active" || status === "pending" ? (
                    <ActionButton
                      onClick={() => showSubscription(member.id)}
                      $variant={buttonVariant}
                    >
                      {buttonText}
                    </ActionButton>
                  ) : needsSeasonSubscription ? (
                    <ActionButton
                      $variant={buttonVariant}
                      onClick={() => navigate("/account/membership")}
                    >
                      {buttonText}
                    </ActionButton>
                  ) : subscriptionStatus === 'closed' ? <></> : (
                    <ActionButton
                      $variant={buttonVariant}
                      onClick={() => showSubscription(member.id)}
                    >
                      {buttonText}
                    </ActionButton>
                  )}
                </ButtonContainer>
              </MemberCard>
            );
          })}

      {unreservedSelections.length > 0 && subscriptionStatus === "open" && (
        <>
          <TotalSection>
            <span>TOTALE</span>
            <span>{indicativeTotal.toFixed(2)} €</span>
          </TotalSection>

          <Button fullWidth onClick={handleReviewReservation}>
            CONTROLLA L'ISCRIZIONE
          </Button>
        </>
      )}
    </PageContainer>
  );
};
